import { Injectable } from '@angular/core';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { SessionRepository } from '@app-core/state/session.repository';
import { createStore, withProps } from '@ngneat/elf';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

export interface ActivityProps {
  totalTime: number;
  isAFK: boolean;
  lastActivity: number;
  lastSync: number;
  sessionStart: number;
  isTracking: boolean;
}

const initialProps: ActivityProps = {
  totalTime: 0,
  isAFK: false,
  lastActivity: Date.now(),
  lastSync: Date.now(),
  sessionStart: Date.now(),
  isTracking: false,
};

const STORE_NAME = 'sandoz-v2-user-usage';

@Injectable({ providedIn: 'root' })
export class ActivityRepository {
  private readonly store;

  private readonly AFK_TIMEOUT = 5 * 60 * 1000; // 5 minutes
  private readonly SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private destroy$ = new Subject<void>();
  private syncTimer$ = new Subject<void>();

  private isStoppingTracking = false;
  private syncInProgress = false;
  private lastTimeUpdate = Date.now();

  private visibilityListener: (() => void) | null = null;
  private beforeUnloadListener: ((event: BeforeUnloadEvent) => void) | null =
    null;
  private pageHideListener: (() => void) | null = null;

  constructor(
    private sessionRepo: SessionRepository,
    private http: HttpClient,
  ) {
    this.store = createStore(
      { name: STORE_NAME },
      withProps<ActivityProps>(initialProps),
    );

    // Subscribe to session state changes
    this.sessionRepo.token_key$
      .pipe(filter(session => !!session))
      .subscribe(() => {
        this.startTracking();
      });
  }

  getStoreProp<K extends keyof ActivityProps>(key: K): ActivityProps[K] {
    return this.store.getValue()[key];
  }

  startTracking(): void {
    if (this.getStoreProp('isTracking')) return;

    // Reset state for new session
    this.store.update(state => ({
      ...state,
      totalTime: 0,
      isAFK: false,
      lastActivity: Date.now(),
      lastSync: Date.now(),
      sessionStart: Date.now(),
      isTracking: true,
    }));

    this.lastTimeUpdate = Date.now();
    this.initializeTracking();
  }

  private initializeTracking(): void {
    // Track user interactions only when tracking is enabled and page is visible
    const userActions$ = merge(
      fromEvent(document, 'mousemove'),
      fromEvent(document, 'mousedown'),
      fromEvent(document, 'keypress'),
      fromEvent(document, 'touchstart'),
      fromEvent(document, 'scroll'),
    );

    userActions$
      .pipe(
        debounceTime(500), // Prevents handling too many events at once
        filter(() => this.store.getValue().isTracking && !document.hidden),
        takeUntil(merge(this.destroy$, this.syncTimer$)),
      )
      .subscribe(() => {
        this.handleUserActivity();
      });

    // Check AFK status every minute
    timer(60000, 60000)
      .pipe(
        filter(() => this.store.getValue().isTracking && !document.hidden),
        takeUntil(merge(this.destroy$, this.syncTimer$)),
      )
      .subscribe(() => {
        this.checkAFKStatus();
      });

    // Sync with backend every 5 minutes, but only when page is visible
    timer(this.SYNC_INTERVAL, this.SYNC_INTERVAL)
      .pipe(
        filter(() => this.store.getValue().isTracking && !document.hidden),
        takeUntil(merge(this.destroy$, this.syncTimer$)),
      )
      .subscribe(() => {
        this.syncWithBackend();
      });

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.visibilityListener = () => {
      if (!this.store.getValue().isTracking) return;

      if (document.hidden) {
        this.handlePageHidden();
      } else {
        this.handlePageVisible();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityListener);

    this.beforeUnloadListener = (_event: BeforeUnloadEvent) => {
      if (this.store.getValue().isTracking) {
        this.updateTimeBeforeSync();
        this.syncWithBackend(true); // Force sync on unload

        return '';
      }

      return undefined;
    };

    this.pageHideListener = () => {
      if (this.store.getValue().isTracking) {
        this.updateTimeBeforeSync();
        this.syncWithBackend(true); // Force sync on hide
      }
    };

    window.addEventListener('beforeunload', this.beforeUnloadListener);
    window.addEventListener('pagehide', this.pageHideListener);
  }

  private sendSynchronousRequest(data: string): void {
    try {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${environment.apiUrl}/me/activity/session-time`, false); // false makes it synchronous
      xhr.setRequestHeader('Content-Type', 'application/json');
      // Add authorization header if needed
      const session = this.sessionRepo.token_key$; // This might need adjustment
      if (session) {
        xhr.setRequestHeader('Authorization', `Bearer ${session}`);
      }
      xhr.send(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to send synchronous request:', e);
    }
  }

  private handleUserActivity(): void {
    if (document.hidden) {
      // Don't update time or reset lastTimeUpdate if page is hidden
      return;
    }

    const now = Date.now();
    const currentState = this.store.getValue();

    if (currentState.isAFK) {
      // If user was AFK and is now active again, just update state and reset timer
      this.store.update(state => ({
        ...state,
        isAFK: false,
        lastActivity: now,
      }));
      this.lastTimeUpdate = now;
    } else {
      // Calculate time since last update
      const timeDiff = now - this.lastTimeUpdate;

      // Only count time if it's a reasonable amount (less than AFK timeout)
      if (timeDiff > 0 && timeDiff < this.AFK_TIMEOUT) {
        const timeIncrease = Math.floor(timeDiff / 1000);

        // Update total time and last activity timestamp
        this.store.update(state => ({
          ...state,
          lastActivity: now,
          totalTime: state.totalTime + timeIncrease,
        }));
      }

      // Always update the lastTimeUpdate to now
      this.lastTimeUpdate = now;
    }
  }

  private checkAFKStatus(): void {
    const currentState = this.store.getValue();
    const now = Date.now();

    // Mark user as AFK if no activity for 5 minutes
    if (
      !currentState.isAFK &&
      now - currentState.lastActivity >= this.AFK_TIMEOUT
    ) {
      this.store.update(state => ({
        ...state,
        isAFK: true,
      }));
    }
  }

  private handlePageHidden(): void {
    // When page becomes hidden, update the lastTimeUpdate
    // without adding to the total time
    this.lastTimeUpdate = Date.now();
  }

  private handlePageVisible(): void {
    const now = Date.now();

    // When page becomes visible again, just update timestamps
    // without adding the time spent while hidden
    this.store.update(state => ({
      ...state,
      lastActivity: now,
    }));

    this.lastTimeUpdate = now;
  }

  // Make sure time is up-to-date before syncing
  private updateTimeBeforeSync(): void {
    const now = Date.now();
    const currentState = this.store.getValue();

    // Only update time if user is active (not AFK) and page is visible
    if (!currentState.isAFK && !document.hidden) {
      const timeDiff = now - this.lastTimeUpdate;

      if (timeDiff > 0 && timeDiff < this.AFK_TIMEOUT) {
        const timeIncrease = Math.floor(timeDiff / 1000);

        this.store.update(state => ({
          ...state,
          lastActivity: now,
          totalTime: state.totalTime + timeIncrease,
        }));

        this.lastTimeUpdate = now;
      }
    }
  }

  private syncWithBackend(force: boolean = false): void {
    if (this.syncInProgress && !force) return;
    if (document.hidden && !force) return;

    this.syncInProgress = true;

    // Update time calculation before syncing
    this.updateTimeBeforeSync();

    const currentState = this.store.getValue();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (force) {
      // Synchronous request for forced syncs (page unload/hide)
      this.sendSynchronousRequest(
        JSON.stringify({
          session_time: currentState.totalTime,
          timezone: timezone,
        }),
      );

      // Reset the total time counter after sync because it is summed in the backend
      this.store.update(state => ({
        ...state,
        totalTime: 0,
        lastSync: Date.now(),
      }));

      this.syncInProgress = false;
    } else {
      // Asynchronous request for regular syncs
      this.http
        .put(`${environment.apiUrl}/me/activity/session-time`, {
          session_time: currentState.totalTime,
          timezone: timezone,
        })
        .subscribe({
          next: () => {
            // Reset the total time counter after sync because it is summed in the backend
            this.store.update(state => ({
              ...state,
              totalTime: 0,
              lastSync: Date.now(),
            }));

            this.syncInProgress = false;
          },
          error: error => {
            // eslint-disable-next-line no-console
            console.error('Failed to sync activity data:', error);
            this.syncInProgress = false;
          },
        });
    }
  }

  stopTracking(): void {
    if (this.isStoppingTracking || !this.store.getValue().isTracking) return;

    this.isStoppingTracking = true;
    this.syncTimer$.next();
    this.syncTimer$.complete();

    // Perform final sync
    this.updateTimeBeforeSync();
    this.syncWithBackend(true);

    // Clean up listeners
    this.cleanupEventListeners();

    this.store.update(state => ({
      ...state,
      isTracking: false,
    }));

    this.isStoppingTracking = false;
  }

  private cleanupEventListeners(): void {
    if (this.visibilityListener) {
      document.removeEventListener('visibilitychange', this.visibilityListener);
      this.visibilityListener = null;
    }

    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }

    if (this.pageHideListener) {
      window.removeEventListener('pagehide', this.pageHideListener);
      this.pageHideListener = null;
    }
  }

  destroy(): void {
    if (this.store.getValue().isTracking) {
      this.stopTracking();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
